import { Auth } from 'aws-amplify';
import httpClient from './httpClient';

// Clinician/admin sign-in and sign-out, recorded server-side in the clinical audit
// (POST /api/v1/auth-event -> AuditEvents). The backend takes the actor from the
// verified token, so the body only says WHAT happened, from a closed vocabulary.
//
// Never throws and never waits long: an audit call must not delay or block a login
// or a logout. A logout that loses its row is a gap in the trail; a logout that
// does not happen is a security problem.

export type LogoutReason = 'user' | 'idle_timeout' | 'password_change';

// Long enough for a healthy round trip, short enough that sign-out still feels instant.
export const AUTH_EVENT_TIMEOUT_MS = 2000;

// Patients can also sign in to the portal, but they are not audit actors (their
// logins go to /app-session) and the authorizer refuses them on this route.
async function isStaffSession(): Promise<boolean> {
  const session = await Auth.currentSession();
  const role = session.getIdToken().payload['custom:v2_role'];
  return typeof role === 'string' && role !== '' && role !== 'patient';
}

async function send(body: { event: 'login' } | { event: 'logout'; reason: LogoutReason }) {
  try {
    if (!(await isStaffSession())) {
      return;
    }
    await httpClient.post('/auth-event', body, { timeout: AUTH_EVENT_TIMEOUT_MS });
  } catch {
    /* best-effort: never let the audit call break sign-in or sign-out */
  }
}

export function recordLogin(): Promise<void> {
  return send({ event: 'login' });
}

// Call BEFORE signing out: once the tokens are gone the call cannot authenticate.
export function recordLogout(reason: LogoutReason): Promise<void> {
  return send({ event: 'logout', reason });
}
